<template>
   <b-col v-bind="settings.container" v-if="ready">
        <div class="row mb-3" v-if="!isQuickEdit()">
            <div class="col-sm-12">
                <b-button variant="secondary" @click="goBackToList">
                    <b-icon icon="arrow-left" class="mr-1"></b-icon>
                    {{ $t('backToList') }}
                </b-button>
            </div>
        </div>
        <form @submit="save">
            <b-tabs lazy :key="version">
                <b-tab v-for="group in fieldGroups" :key="group">
                    <template slot="title">
                        <span v-if="group == ''">{{ $t('default') }}</span>
                        <span v-else>{{ groupName(group) }}</span>
                    </template>

                    <component :is="wrapperComponentElement" v-bind="wrapperComponent">
                        <b-col cols="12" align-self="center" v-for="value in sortedFields" :key="value.key">
                            <b-row :class="{fieldEditing: visibleFieldSettings}" v-if="value.visible && canAccessField(value, 'read')">
                                    <b-col :cols="visibleFieldSettings ? 11 : 12">
                                        <IdentityFields :label="$translateLabel(value.label, value.name)" :field="value" v-model="input" @error="fieldError"  :disabled="value.protected || !canAccessField(value, 'update')" page="model_edit" :extendErrors="httpErrors[value.key]" @input="cleanErrors" :store="$store" :context="context" :translator="$t" v-if="value.visible && (value.group || '') == group">
                                            <template v-slot:IdentityFieldSelectRelation v-if="value.module == 'select' && value.type == 'relation' && !settings.slotless">
                                                <SelectRelationUI :field="value" v-model="input" :key="value.key" />
                                            </template>

                                            <template v-slot:IdentityFieldImageCarousel="{ itemSelected, mode }" v-if="value.module == 'image' && value.type == 'carousel' && !settings.slotless">
                                                <MediaLibrary @itemsSelected="itemSelected" :mode="mode" />
                                            </template>

                                            <template v-slot:ProvidedTemplate>
                                                <ComponentView :id="value.provided_template_component" :entrypoint="value.component_template" :field="value" :context="context" v-model="input" :key="value.key" page="model_edit"/>
                                            </template>
                                        </IdentityFields>
                                    </b-col>

                                    <b-col cols="1" align-self="center">
                                        <b-icon icon="gear-wide" v-if="visibleFieldSettings" @click="startFieldEdit(value)"/>
                                    </b-col>                           
                            </b-row>

                        </b-col>
                    </component>

                    <template v-if="!model">
                        <b-form-group>
                            <b-skeleton :width="`${getSkeletonWidth()}%`" :height="`${getSkeletonHeight()}px`" class="fieldSkeleton" animation="throb"/>
                        </b-form-group>
                        <b-form-group>
                            <b-skeleton :width="`${getSkeletonWidth()}%`" :height="`${getSkeletonHeight()}px`" class="fieldSkeleton" animation="throb"/>
                        </b-form-group>
                        <b-form-group>
                            <b-skeleton :width="`${getSkeletonWidth()}%`" :height="`${getSkeletonHeight()}px`" class="fieldSkeleton" animation="throb"/>
                        </b-form-group>
                    </template>

                </b-tab>

                <b-tab v-for="ext in activeExtensions" :key="'ext-' + ext.name">
                    <template slot="title">
                        <b-icon icon="puzzle" class="mr-1"/>
                        {{ ext.name }}
                    </template>
                    <b-row>
                        <b-col cols="12" align-self="center" v-for="value in fields.filter(function(f){ return f.extensionName === ext.name })" :key="value.key">
                            <b-row v-if="value.visible && canAccessField(value, 'read')">
                                <b-col cols="12">
                                    <IdentityFields :label="$translateLabel(value.label, value.name)" :field="value" v-model="input" @error="fieldError" :disabled="value.protected || !canAccessField(value, 'update')" page="model_edit" :extendErrors="httpErrors[value.key]" @input="cleanErrors" :store="$store" :context="context" :translator="$t">
                                        <template v-slot:IdentityFieldSelectRelation v-if="value.module == 'select' && value.type == 'relation' && !settings.slotless">
                                            <SelectRelationUI :field="value" v-model="input" :key="value.key" />
                                        </template>
                                        <template v-slot:IdentityFieldImageCarousel="{ itemSelected, mode }" v-if="value.module == 'image' && value.type == 'carousel' && !settings.slotless">
                                            <MediaLibrary @itemsSelected="itemSelected" :mode="mode" />
                                        </template>
                                        <template v-slot:ProvidedTemplate>
                                            <ComponentView :id="value.provided_template_component" :entrypoint="value.component_template" :field="value" :context="context" v-model="input" :key="value.key" page="model_edit"/>
                                        </template>
                                    </IdentityFields>
                                </b-col>
                            </b-row>
                        </b-col>
                    </b-row>
                </b-tab>

                <b-tab v-if="model">
                    <template slot="title">
                        <span>{{ $t('systemInfo') }}</span>
                    </template>
                    <b-row>
                        <b-col cols="12" align-self="center">
                            <b-form-group
                                :label="$t('createdBy')"
                                :horizontal="true">
                                <b-form-input :disabled="true" type="text" :value="model.created_by"></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col cols="12">
                            <b-form-group
                                :label="$t('createdAt')"
                                :horizontal="true">
                                <b-form-input :disabled="true" type="text" :value="$toUserTimeZone(model.created_at)"></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col cols="12">
                            <b-form-group
                                :label="$t('updatedBy')"
                                :horizontal="true">
                                <b-form-input :disabled="true" type="text" :value="model.updated_by"></b-form-input>
                            </b-form-group>    
                        </b-col>
                        
                        <b-col cols="12">
                            <b-form-group
                                :label="$t('updatedAt')"
                                :horizontal="true">
                                <b-form-input :disabled="true" type="text" :value="$toUserTimeZone(model.updated_at)"></b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-tab>
            </b-tabs>
            <div class="row" v-if="model">
                <slot name="controls" :save="save" :remove="remove" :deleteButtonBudy="deleteButtonBusy" :component="component" :saveButtonBusy="saveButtonBusy">
                    <div class="col-sm-3" v-if="$can('edit ' + component.general.getRef())">
                        <b-button type="submit" class="btn btn-success btn-block active mt-2 mr-2" v-on:click="save">
                            <b-spinner small v-if="saveButtonBusy"></b-spinner>
                            <span v-if="!saveButtonBusy">{{ $t('save') }}</span>
                        </b-button>
                    </div>
                    <div class="col-sm-3" v-if="$can('delete ' +  component.general.getRef())">
                        <b-button type="button" class="btn btn-danger btn-block active mt-2 mr-2" v-on:click="remove">
                            <b-spinner small v-if="deleteButtonBusy"></b-spinner>
                            <span v-if="!deleteButtonBusy">{{ $t('delete') }}</span>
                            </b-button>
                    </div>
                </slot>
            </div>

            <b-sidebar id="template" right title="Fields" v-model="visibleFieldSettings" v-if="$can('settings ' + this.component.general.getRef()) && $isPlatformEnv('dev')">
                <DraggableFields :group="{ name: 'fields', pull: 'clone', put: false }" :mutateClone="onClone" @dragStart="draggingInProcess = true" @dragEnd="draggingInProcess = false"/>
            </b-sidebar>

            <b-sidebar right v-model="editingField.show">
                <FieldEdit v-model="editingField.field" :key="version" v-if="editingField.field" :fields="fields" :component="component"></FieldEdit>

                <template #footer>
                    <div class="d-flex bg-dark text-light align-items-center px-3 py-2">
                        <b-button variant="success" size="sm" @click="editField">{{ $t('save') }}</b-button>
                        <b-button variant="danger" size="sm" @click="removeField" class="ml-1">{{ $t('delete') }}</b-button>
                    </div>
                </template>
            </b-sidebar>
        </form>

        <main-dialog title="Performing action" v-model="actionModal.processing"
        o-close-on-esc no-close-on-backdrop  hide-header-close hide-footer 
        >
            <div class="d-flex align-items-center">
                {{ actionModal.actionName }}
                <b-spinner variant="primary" class="ml-4"></b-spinner>
            </div>
        </main-dialog>
    </b-col>

    <b-skeleton width="100%" height="250px" v-else animation="throb"/>
</template>

<script>

import { 
    Thunder,
    Context,
    Model,
    Workflow
} from '@protokol/platform-libs'

import IdentityFields from '@/components/identityFields/IdentityFields'
import safeEval from 'safe-eval'
import _ from "lodash"
import moment from 'moment'
import DraggableFields from './Settings/DraggableFields.vue'
import FieldEdit from './Settings/FieldEdit.vue'
import draggable from 'vuedraggable'
import SelectRelationUI from '../identityFields/ui/SelectRelation.vue'
import MediaLibrary from '@/components/integrations/protokol/media/Index'
import ComponentView from './ComponentView.vue'
import Component from '@/lib/Component'

export default {
    name: "DefaultEdit",
    components: {
        IdentityFields,
        DraggableFields,
        FieldEdit,
        draggable,
        SelectRelationUI,
        ComponentView,
        MediaLibrary
    },
    props: {
        id: {
            type: String,
            default: null
        },
        modelUuid: {
            type: String,
            default: null
        },
        component: {
            type: Component
        },
        settings: {
            type: Object,
            default: () => {
                return {
                    container: {
                        cols: 12,
                        md: 6
                    },

                    slotless: false
                }
            }
        }
    },
    data: () => {
        return {
            ready: false,
            model: {},
            aModel: {},
            oldModel: '',
            saveButtonBusy: false,
            deleteButtonBusy: false,
            enableButtonBusy: false,
            errors: [],
            actionModal: {
                processing: false,
                actionName: ''
            },
            httpErrors: [],
            actionPopup: {
                show: false,
                action: {}
            },
            version: 0,
            visibleFieldSettings: false,
            editingField: {
                field: null,
                fieldOld: null,
                show: false
            },
            draggingInProcess: false
        }
    },

    computed: {
        input: {
            get() {
                return this.model
            },
            
            set(e) {
                if (typeof e !== 'object') {
                    console.error(`Invalid input. If you are trying to emit input event then you should try to pass an object. Ex: {field: value}`)
                    return
                }

                const data = e || {}
                let model = this.model || {}

                // here we need to deep merge the model with data
                // arrays must be replaced, not merged by index (lodash default)
                this.model = _.mergeWith({}, model, data, function(objVal, srcVal) {
                    if (Array.isArray(srcVal)) {
                        return srcVal
                    }
                })
                this.$emit('modelChanged', this.model)
            }
        },

        actionInput: {
            get() {
                return this.aModel
            },
            
            set(e) {
                this.aModel = {...this.aModel, ...e}
            }
        },
        
        sortedFields: {
            set(val) {
                // Filter out extension fields - they are read-only
                const regularFields = val.filter(f => !f.isExtensionField)
                this.component.settings.fields = regularFields

                this.$store.dispatch("updateComponentSettings", this.component).then((settings) => {
                    this.component = settings
                })
            },

            get() {
                // Extension fields are shown in their own tabs, not here
                let fields = _.cloneDeep(this.fields || []).filter(f => !f.isExtensionField)

                // Filter by schema if schema is selected
                const ref = this.$route ? this.$route.meta.ref : null
                if (ref) {
                    const storageKey = `component_${ref}_schema`
                    const selectedSchema = localStorage.getItem(storageKey)
                    
                    if (selectedSchema !== null) {
                        fields = fields.filter(f => (f.schema || '') === selectedSchema)
                    }
                }

                fields.sort((a, b) => {
                    return a.sortOrder - b.sortOrder
                })

                return fields
            }
        },

        draggableFields() {
            if (this.visibleFieldSettings) {
                return this.sortedFields
            }

            return []
        },

        fieldGroups() {
            try {
                const groups = {}
                
                this.sortedFields.forEach(f => {
                    if (!f.visible) return
                    const group = f.group || ''
                    groups[group] = true
                })

                return Object.keys(groups);
            } catch(err) {
                this.$debugLog(err)

                return ['']
            }
        },

        activeExtensions() {
            if (!this.component || !this.component.settings || !this.component.settings.extensions) {
                return []
            }
            return this.component.settings.extensions.filter(ext => ext.is_active && ext.fields && ext.fields.length > 0)
        },

        context() {
            return new Context({
                model: _.cloneDeep(this.model), 
                locale: this.$store.getters.getLocale
            })
        },

        fields() {
            console.log("Getting all fields from component", this.component.getAllFields())
            return this.component.getAllFields()
        },

        wrapperComponent() {
            if (this.visibleFieldSettings) {
                return {
                    tag: 'div',
                    class: {
                        row: true
                    },
                    'v-model': "sortedFields",
                    group: "fields"
                }
            }

            return {}
        },

        wrapperComponentElement() {
            if (this.visibleFieldSettings) {
                return 'draggable'
            }

            return 'b-row'
        }
    },
    methods: {
        goBackToList() {
            if (this.$route) {
                // Navigate back to the list
                const pathParts = this.$route.path.split('/')
                pathParts.pop() // Remove 'edit'
                pathParts.pop() // Remove model uuid
                const listPath = pathParts.join('/')
                this.$router.push({ path: listPath })
            }
        },

        isModelChanged() {
            return JSON.stringify(this.model) != JSON.stringify(this.oldModel)
        },

        onMove(e) {
            console.log("onMove", e)
        },

        onClone(original) {
            original.name = `Field${this.sortedFields.length}`
            original.key = `field${this.sortedFields.length}`
            original.group = this.fieldGroups[this.currentTab]

            return original;
        },

        startFieldEdit(field) {
            this.editingField.show = true
            this.editingField.field = field
            this.editingField.fieldOld = _.cloneDeep(field)
        },

        removeField() {
            this.$mainDialog.msgBoxConfirm('Are you sure?').then((value) => {
                if (value) {
                    const fields = this.sortedFields.filter(f => {
                        return f.key != this.editingField.field.key
                    })

                    this.sortedFields = fields
                    this.editingField.show = false
                }
            })
        },

        editField() {
            let field = this.editingField.field
            let oldField = this.editingField.fieldOld

            let fields = this.sortedFields.map(el => {
                if (el.key == oldField.key) {
                return field
                } else {
                return el
                }
            });

            this.editingField.show = false

            this.sortedFields = fields

            this.version++
        },

        groupName(group) {
            return _.capitalize(group)
        },

        fieldError(error) {
            let found = this.errors.find((i, e) => {
                return i.field == error.field
            })

            if (found === undefined) {
                this.errors.push(error)
            } else {
                this.errors.map(e => {
                    if (found.field == e.field)
                        e.error = error.error
                })
            }
        },
        checkForErrors() {
            console.log("Checking for errors", this.errors)
            return this.errors.find((i, e) => {
                return i.error
            })
        },
        save(e) {
            e.preventDefault()
            
             if (this.checkForErrors()) {
                this.$bvToast.toast(this.$t('fieldsNotValid'), {
                    variant: 'danger',
                    title: this.$t('error')
                })
                this.saveButtonBusy = false

                return
            }


            const componentUuid = this.getComponentUUID()

            const popup = this.$loadingPopup()

            this.$component(componentUuid).store().update(this.getModelUUID(), {
                version: this.model.__version__,
                data: this.model
            }).then((resp) => {
                this.$bvToast.toast(this.$t('Saved'), {
                    variant: 'success',
                    title: this.$t('success')
                })

                this.model = resp.data

                this.$store.commit("SET_COMPONENTS_MODEL", {uuid:componentUuid, results: this.model})

                this.$emit('saved', this.model)

                this.oldModel = _.cloneDeep(this.model)
            }).catch((error) => {
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }

                    if (error.response.status == 417) {
                        this.httpErrors = error.response.data.errors
                        return
                    }

                    if (error.response.status == 409) {
                        this.$bvToast.toast(this.$t('concurrentModelUpdate'), {
                            variant: 'warning'
                        })
                        return
                    }

                    this.$bvToast.toast(error.response.data.message, {
                        variant: 'danger'
                    })
                }


                this.$debugLog(error)
            }).finally(() => {
                popup.hide()
            })
        },

        remove() {
            let componentUuid = this.getComponentUUID()

            this.$mainDialog.msgBoxConfirm(this.$t('areYouSure')).then(resp => {
                if (resp) {
                    this.deleteButtonBusy = true
                    this.$component(componentUuid).store().delete(this.getModelUUID()).then(() => {
                        this.deleteButtonBusy = false
                        this.$store.commit("REMOVE_COMPONENTS_MODEL", {uuid: componentUuid, model_uuid: this.getModelUUID()})
                        this.$router.back()
                    }).catch((error) => {
                        if (error.response !== undefined) {
                            if (error.response.status == 500) {
                                this.$serverErrorModal(error)
                                this.deleteButtonBusy = false
                                return
                            }

                            this.$bvToast.toast(error.response.data.message, {
                                variant: 'danger'
                            })
                        }
                        

                        this.deleteButtonBusy = false
                    })
                }
            })
        },

        actionClick(action) {
            let { fields = [] } = action

            if (fields === null) {
                this.doAction(action)
                return
            }


            if (fields.length > 0) {
                this.openActionPopup(action) 
            } else {
                this.doAction(action)
            }
        },

        openActionPopup(action) {
            this.actionPopup.show = true;
            this.actionPopup.action = action;
        },

        doAction(action) {
            this.actionModal.processing = true;
            this.actionModal.actionName = action.name;

            let cont = safeEval("(async function() { try {"+action.action+" return model; } catch (error) { return error; } }());", {
                model: new Model(this.model, {locale: this.$store.getters.getLocale}),
                _: _,
                moment: moment,
                input: this.aModel,
                thunder: new Thunder(this.$store),
                workflow: new Workflow(this.$store, this.getComponentUUID(), this.model.uuid)
            })

            Promise.resolve(cont).then(val => {
                if (val && val.stack && val.message) {
                    this.actionModal.processing = false
                    this.$mainDialog.msgBoxOk("Action "+ action.name +" evaluation error: " + val.message)
                    return
                }

                if (val !== undefined) {
                    this.actionModal.processing = false;
                    this.model = val
                }
            
            }).catch(err => {
                this.$mainDialog.msgBoxOk("Action "+ action.name +" evaluation error: " + err.message)
            })

        },

        cleanErrors() {
            this.httpErrors = []
        },

        isQuickEdit() {
            return !this.$router
        },

        getComponentUUID() {
            if (this.id) {
                return this.id
            }
            
            let ref = this.$route ? this.$route.meta.ref : null
            
            // Include schema in the ref if schema is selected
            if (ref) {
                const storageKey = `component_${ref}_schema`
                const selectedSchema = localStorage.getItem(storageKey)
                
                if (selectedSchema) {
                    ref = `${ref}.${selectedSchema}`
                }
            }
            
            return ref
        },

        getModelUUID() {
            return this.modelUuid
        },

        lgSize() {
            return this.isQuickEdit() ? 12 : 6
        },

        getSkeletonWidth() {
            return _.random(20, 100)
        },

        getSkeletonHeight() {
            return _.random(5, 30)
        },

        canAccessField(field, action) {
            // Get user roles from claims
            const claims = this.$store.getters.getClaims
            if (!claims || !claims.Roles) {
                return true // Default to visible if no claims
            }
            const userRoles = claims.Roles || []

            // Get policies from component settings
            const policies = (this.component && this.component.settings && this.component.settings.policies) ? this.component.settings.policies : []
            
            // Filter field_access policies that are enabled
            const fieldAccessPolicies = policies.filter(p => 
                p.type === 'field_access' && 
                p.enabled === true
            )

            if (fieldAccessPolicies.length === 0) {
                return true // No policies, field is visible
            }

            // Check if any policy grants access
            let hasAccess = false
            const fieldKey = field.key
            const fieldKeyWithExtension = field.extensionName ? `${field.extensionName}.${field.key}` : field.key

            for (const policy of fieldAccessPolicies) {
                const config = policy.config || {}
                const policyFields = config.fields || []
                const policyRoles = config.roles || []
                const policyActions = config.actions || []

                // Check if this policy applies to this field
                const appliesToField = policyFields.includes(fieldKey) || policyFields.includes(fieldKeyWithExtension)
                
                if (appliesToField && policyActions.includes(action)) {
                    // Check if user has any of the required roles
                    const hasRole = policyRoles.some(role => userRoles.includes(role))
                    if (hasRole) {
                        hasAccess = true
                        break
                    }
                }
            }

            return hasAccess
        }
    },

    updated() {
        console.log("[Render] DefaultEdit rendered")
    },

    created() {
        let componentUuid = this.getComponentUUID()
        this.model = _.cloneDeep(this.$store.getters.componentModel(componentUuid, this.getModelUUID()))
        if (!this.model) {
            this.$component(componentUuid).store().getModelByUUID(this.getModelUUID(), {}).then(resp => {
                this.model = _.cloneDeep(resp.data)
                this.oldModel = _.cloneDeep(this.model)
                this.ready = true
                this.$emit('modelChanged', this.model)
            }).catch((error) => {
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }

                    this.$bvToast.toast(error.response.data.message, {
                        variant: 'danger'
                    })
                    return
                }

                this.$debugLog(error)
                
            })
        }

    }
}
</script>

<style lang="scss" scoped>
.fieldEditing {
    border-bottom: 10px solid #2C3135;
    transition: border-width 0.5s linear;
    margin-left: -17px;
    margin-right: -17px;
}
</style>